import { initializeApp } from 'firebase/app';
import { getFirestore, collection, addDoc, serverTimestamp } from 'firebase/firestore';
import emailjs from '@emailjs/browser';

import { COMPANY } from '../constants/knowledge';

export interface LeadFormData {
  projectType: string;
  budget: string;
  timeline: string;
  name: string;
  email: string;
  phone: string;
  city: string;
}

/**
 * Generates a pre-filled WhatsApp direct chat link with the user's lead inquiry details.
 */
export function generateWhatsAppLeadUrl(data: LeadFormData, whatsappNumber: string = COMPANY.whatsapp): string {
  const lines = [
    `Hi ${COMPANY.name}!`,
    `I just requested a design consultation on your website:`,
    ``,
    `• *Name:* ${data.name}`,
    `• *Phone:* ${data.phone}`,
    data.email ? `• *Email:* ${data.email}` : null,
    `• *Location:* ${data.city}`,
    `• *Project Type:* ${data.projectType || 'Not specified'}`,
    `• *Budget Range:* ${data.budget || 'Not specified'}`,
    `• *Timeline:* ${data.timeline || 'Not specified'}`,
    ``,
    `Could you please share more details and next steps?`
  ].filter(line => line !== null);

  const message = lines.join('\n');
  return `https://wa.me/${whatsappNumber}?text=${encodeURIComponent(message)}`;
}

type Provider = 'google_sheets' | 'emailjs' | 'firebase';

const PROVIDER: Provider = (import.meta.env.VITE_FORM_PROVIDER as Provider) || 'google_sheets';

// --- Google Sheets ---
async function submitToGoogleSheets(data: LeadFormData) {
  const url = import.meta.env.VITE_GOOGLE_SHEETS_URL;
  if (!url) throw new Error('VITE_GOOGLE_SHEETS_URL is not set');

  const now = new Date();
  const ist = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Asia/Kolkata',
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', hour12: false,
  }).format(now).replace(',', '');

  const payload = {
    timestamp: ist,
    name: data.name,
    email: data.email,
    phone: data.phone,
    city: data.city,
    projectType: data.projectType,
    budget: data.budget,
    timeline: data.timeline || 'Not specified',
  };
  // console.log('Submitting payload:', payload);
  // Use no-cors to avoid redirect/CORS issues with Apps Script
  await fetch(url, {
    method: 'POST',
    mode: 'no-cors',
    headers: { 'Content-Type': 'text/plain' },
    body: JSON.stringify(payload),
  });
}

// --- EmailJS ---
async function submitToEmailJS(data: LeadFormData) {
  const serviceId = import.meta.env.VITE_EMAILJS_SERVICE_ID;
  const templateId = import.meta.env.VITE_EMAILJS_TEMPLATE_ID;
  const publicKey = import.meta.env.VITE_EMAILJS_PUBLIC_KEY;
  if (!serviceId || !templateId || !publicKey) throw new Error('EmailJS env vars not set');

  return emailjs.send(serviceId, templateId, {
    from_name: data.name,
    from_email: data.email,
    phone: data.phone,
    city: data.city,
    project_type: data.projectType,
    budget: data.budget,
    timeline: data.timeline,
  }, publicKey);
}

// --- Firebase ---
let db: ReturnType<typeof getFirestore> | null = null;

function getFirestoreDb() {
  if (db) return db;
  const app = initializeApp({
    apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
    authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
    projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
    storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
    messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
    appId: import.meta.env.VITE_FIREBASE_APP_ID,
  });
  db = getFirestore(app);
  return db;
}

async function submitToFirebase(data: LeadFormData) {
  if (!import.meta.env.VITE_FIREBASE_PROJECT_ID) throw new Error('Firebase env vars not set');
  const firestore = getFirestoreDb();
  return addDoc(collection(firestore, 'leads'), { ...data, createdAt: serverTimestamp() });
}

// --- Meta WhatsApp Cloud API Direct Integration ---
async function sendMetaWhatsAppNotification(data: LeadFormData): Promise<void> {
  const phoneId = import.meta.env.VITE_META_WHATSAPP_PHONE_ID;
  const token = import.meta.env.VITE_META_WHATSAPP_TOKEN;
  const recipientPhone = import.meta.env.VITE_OPS_WHATSAPP_PHONE || COMPANY.whatsapp || '919989697942';

  if (!phoneId || !token) return;

  try {
    const textBody = [
      `🔔 *NEW WEBSITE LEAD*`,
      ``,
      `• *Name:* ${data.name}`,
      `• *Phone:* ${data.phone}`,
      data.email ? `• *Email:* ${data.email}` : null,
      `• *Location:* ${data.city}`,
      `• *Project Type:* ${data.projectType}`,
      `• *Budget:* ${data.budget}`,
      `• *Timeline:* ${data.timeline || 'Not specified'}`
    ].filter(Boolean).join('\n');

    const cleanPhone = recipientPhone.replace(/[^0-9]/g, '');

    const response = await fetch(`https://graph.facebook.com/v20.0/${phoneId}/messages`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        messaging_product: 'whatsapp',
        recipient_type: 'individual',
        to: cleanPhone,
        type: 'text',
        text: {
          preview_url: false,
          body: textBody,
        },
      }),
    });

    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      console.warn('Meta WhatsApp Cloud API response not OK:', err);
    }
  } catch (err) {
    console.warn('Meta WhatsApp Cloud API dispatch error:', err);
  }
}

// --- Ops WhatsApp Notification ---
async function sendOpsWhatsAppNotification(data: LeadFormData): Promise<void> {
  const opsPhone = import.meta.env.VITE_OPS_WHATSAPP_PHONE || COMPANY.whatsapp || '919989697942';
  const callmebotApiKey = import.meta.env.VITE_CALLMEBOT_API_KEY;
  const webhookUrl = import.meta.env.VITE_OPS_WHATSAPP_WEBHOOK;

  // 1. Meta WhatsApp Cloud API (Direct Official Meta API)
  await sendMetaWhatsAppNotification(data);

  // 2. CallMeBot integration (Fallback Free WhatsApp alert)
  if (callmebotApiKey) {
    try {
      const msg = [
        `🔔 *NEW LEAD ALERT*`,
        ``,
        `• *Name:* ${data.name}`,
        `• *Phone:* ${data.phone}`,
        data.email ? `• *Email:* ${data.email}` : null,
        `• *City:* ${data.city}`,
        `• *Project:* ${data.projectType}`,
        `• *Budget:* ${data.budget}`,
        `• *Timeline:* ${data.timeline || 'Not specified'}`
      ].filter(Boolean).join('\n');

      const url = `https://api.callmebot.com/whatsapp.php?phone=${opsPhone}&text=${encodeURIComponent(msg)}&apikey=${callmebotApiKey}`;
      await fetch(url, { method: 'GET', mode: 'no-cors' });
    } catch (err) {
      console.warn('CallMeBot notification failed:', err);
    }
  }

  // 3. Custom Webhook integration (Make / Zapier / UltraMsg / Slack / etc.)
  if (webhookUrl) {
    try {
      await fetch(webhookUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...data,
          source: 'Website Lead Form',
          submittedAt: new Date().toISOString()
        }),
        mode: 'no-cors'
      });
    } catch (err) {
      console.warn('Ops webhook notification failed:', err);
    }
  }
}

// --- Main export ---
export async function submitLeadForm(data: LeadFormData): Promise<void> {
  // Save to local history as a general safety measure (prevents loss if opaque POSTs fail silently)
  try {
    const history = JSON.parse(localStorage.getItem('aura_leads_history') || '[]');
    history.push({
      ...data,
      timestamp: new Date().toISOString(),
    });
    localStorage.setItem('aura_leads_history', JSON.stringify(history));
  } catch (e) {
    console.error('Failed to save lead history to localStorage:', e);
  }

  try {
    switch (PROVIDER) {
      case 'google_sheets':
        await submitToGoogleSheets(data);
        break;
      case 'emailjs':
        await submitToEmailJS(data);
        break;
      case 'firebase':
        await submitToFirebase(data);
        break;
      default:
        throw new Error(`Unknown form provider: ${PROVIDER}`);
    }

    // Trigger ops notification in background
    sendOpsWhatsAppNotification(data).catch(err => {
      console.warn('Ops notification background error:', err);
    });
  } catch (error) {
    // Backup explicitly failed submissions to an offline queue
    try {
      const backupQueue = JSON.parse(localStorage.getItem('offline_leads_backup') || '[]');
      backupQueue.push({
        ...data,
        failedAt: new Date().toISOString(),
        error: error instanceof Error ? error.message : String(error)
      });
      localStorage.setItem('offline_leads_backup', JSON.stringify(backupQueue));
      console.warn('Lead submission failed. Saved to offline backup queue:', data, error);
    } catch (backupErr) {
      console.error('Failed to save to offline backup queue:', backupErr);
    }
    throw error;
  }
}

